/**
 * One page serving both roles.
 *
 * A viewer follows the controller by default and may rename a speaker. A
 * controller additionally broadcasts where it is looking and where it is
 * pointing, coalesced to the display frame rate.
 *
 * The two differ in behaviour, not in layout — which is the point: what the
 * phone does to the transcript is what every viewer sees happen to theirs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEventOf, type Envelope } from '@lt/protocol';
import { ConversationView } from '../components/ConversationView.js';
import { TouchOverlay } from '../components/TouchOverlay.js';
import { useSession } from '../hooks/useSession.js';
import { endSession } from '../lib/api.js';
import { FollowState, anchorFromViewport, type SegmentBox } from '../lib/scroll-anchor.js';
import { TouchTrace, normalisePointer } from '../lib/touch-trace.js';
import { speakerColorIndex, speakerName } from '../lib/transcript-state.js';
import { clock, meetingTitle, speakersHeard, talkShare, toMarkdown } from '../lib/transcript-view.js';

/** The pointer's ink: the same magenta the status line uses for "following". */
const POINTER_COLOR = '#e0218a';

export interface SessionPageProps {
  sessionId: string;
  token: string;
  role: 'viewer' | 'controller';
}

export function SessionPage({ sessionId, token, role }: SessionPageProps): JSX.Element {
  const { state, connection, send, recordLatency, onEvent } = useSession({ sessionId, token, role });

  const columnRef = useRef<HTMLDivElement | null>(null);
  const boxesRef = useRef<SegmentBox[]>([]);
  const follow = useMemo(() => new FollowState(), []);
  const trace = useMemo(() => new TouchTrace(), []);
  const [following, setFollowing] = useState(true);
  const [isPointing, setIsPointing] = useState(false);

  const viewportOf = useCallback((element: HTMLDivElement) => ({
    scrollTop: element.scrollTop,
    clientHeight: element.clientHeight,
    scrollHeight: element.scrollHeight,
  }), []);

  /* ---------------------------------------------------------------- viewer */

  // Incoming follow events. Deliberately not routed through the reducer: they
  // must not re-render the transcript 60 times a second.
  useEffect(() => {
    if (role !== 'viewer') return;
    return onEvent((event: Envelope) => {
      const element = columnRef.current;
      if (!element) return;

      if (isEventOf(event, 'scroll.anchor')) {
        recordLatency('scroll', Date.now() - event.payload.sentAt);
        const target = follow.receive(event.payload, boxesRef.current, viewportOf(element));
        if (target !== null) element.scrollTop = target;
        setFollowing(follow.following);
      }

      if (isEventOf(event, 'touch.point')) {
        recordLatency('touch', Date.now() - event.payload.sentAt);
        trace.apply(event.payload);
      }
    });
  }, [role, onEvent, follow, trace, recordLatency, viewportOf]);

  /* ------------------------------------------------------------ controller */

  const lastSentAnchor = useRef<string>('');
  const pointerActive = useRef(false);

  const broadcastAnchor = useCallback(() => {
    if (role !== 'controller') return;
    const element = columnRef.current;
    if (!element) return;
    const anchor = anchorFromViewport(boxesRef.current, viewportOf(element));
    if (!anchor) return;
    // Round the fraction before deduping: a sub-pixel change is not news, and
    // the relay coalesces anyway.
    const key = `${anchor.segmentId}:${anchor.fraction.toFixed(3)}`;
    if (key === lastSentAnchor.current) return;
    lastSentAnchor.current = key;
    send('scroll.anchor', anchor);
  }, [role, send, viewportOf]);

  // One rAF loop, so a 120 Hz thumb cannot outpace the display.
  const scrollDirty = useRef(false);
  useEffect(() => {
    if (role !== 'controller') return;
    let frame = requestAnimationFrame(function tick() {
      frame = requestAnimationFrame(tick);
      if (!scrollDirty.current) return;
      scrollDirty.current = false;
      broadcastAnchor();
    });
    return () => cancelAnimationFrame(frame);
  }, [role, broadcastAnchor]);

  const handleScroll = useCallback(
    (element: HTMLDivElement) => {
      if (role === 'controller') {
        scrollDirty.current = true;
        return;
      }
      // A viewer scrolling by hand steps out of follow mode.
      follow.onScroll(element.scrollTop);
      setFollowing(follow.following);
    },
    [role, follow],
  );

  const sendTouch = useCallback(
    (clientX: number, clientY: number, touchState: 'down' | 'move' | 'up') => {
      const element = columnRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const normalised = normalisePointer(
        { x: clientX, y: clientY },
        { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        boxesRef.current,
        element.scrollTop,
      );
      send('touch.point', { ...normalised, state: touchState, sentAt: Date.now() });
      // The controller draws its own trace locally, so it sees exactly what the
      // viewers are being shown.
      trace.apply({ ...normalised, state: touchState, sentAt: Date.now() });
    },
    [send, trace],
  );

  const pointerHandlers =
    role === 'controller'
      ? {
          onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
            // Only a deliberate press points; a two-finger scroll should scroll.
            if (event.pointerType === 'mouse' && event.buttons !== 1) return;
            pointerActive.current = true;
            setIsPointing(true);
            event.currentTarget.setPointerCapture(event.pointerId);
            sendTouch(event.clientX, event.clientY, 'down');
          },
          onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
            if (!pointerActive.current) return;
            sendTouch(event.clientX, event.clientY, 'move');
          },
          onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
            if (!pointerActive.current) return;
            pointerActive.current = false;
            setIsPointing(false);
            sendTouch(event.clientX, event.clientY, 'up');
          },
          onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => {
            if (!pointerActive.current) return;
            pointerActive.current = false;
            setIsPointing(false);
            // A cancelled gesture must still release the dot on every viewer.
            sendTouch(event.clientX, event.clientY, 'up');
          },
        }
      : {};

  /* ----------------------------------------------------------------- shared */

  const handleGeometry = useCallback(
    (boxes: SegmentBox[], element: HTMLDivElement) => {
      boxesRef.current = boxes;
      if (role === 'viewer') {
        // New segments have just been laid out: an anchor that was waiting for
        // one of them can now be applied.
        const target = follow.retryPending(boxes, viewportOf(element));
        if (target !== null) element.scrollTop = target;
      }
    },
    [role, follow, viewportOf],
  );

  const overlayGeometry = useCallback(() => {
    const element = columnRef.current;
    if (!element) return null;
    return {
      boxes: boxesRef.current,
      scrollTop: element.scrollTop,
      width: element.clientWidth,
      height: element.clientHeight,
    };
  }, []);

  const renameSpeaker = useCallback(
    (speakerKey: string, currentName: string) => {
      const next = window.prompt(`Name for ${currentName}?`, currentName);
      if (next === null) return;
      const trimmed = next.trim();
      if (!trimmed || trimmed === currentName) return;
      send('speaker.rename', { speakerKey, displayName: trimmed });
    },
    [send],
  );

  const resumeFollowing = useCallback(() => {
    follow.resume();
    setFollowing(true);
    const element = columnRef.current;
    if (!element) return;
    const target = follow.retryPending(boxesRef.current, viewportOf(element));
    if (target !== null) element.scrollTop = target;
  }, [follow, viewportOf]);

  const speakerList = speakersHeard(state);
  const shares = useMemo(() => talkShare(state.segments), [state.segments]);
  // A past meeting is read, not followed: the live status has nothing to say.
  const ended = state.status === 'ended';
  const started = state.startedAt === null ? null : new Date(state.startedAt);

  // Export. Taken from what this browser holds, which after the meeting is the
  // corrected transcript — the version worth keeping.
  const [copiedTranscript, setCopiedTranscript] = useState(false);
  const exportTitle = meetingTitle(state.startedAt);
  const downloadTranscript = useCallback(() => {
    const blob = new Blob([toMarkdown(state, exportTitle)], { type: 'text/markdown' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `meeting-${sessionId}.md`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [state, exportTitle, sessionId]);
  const copyTranscript = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(toMarkdown(state, exportTitle));
      setCopiedTranscript(true);
      setTimeout(() => setCopiedTranscript(false), 1_500);
    } catch {
      downloadTranscript();
    }
  }, [state, exportTitle, downloadTranscript]);

  return (
    <div className={`session ${role}${ended ? ' ended' : ''}`}>
      <header className="masthead">
        <div className="heading">
          <span className="eyebrow">
            {ended ? 'Meeting record' : 'Live meeting'}
            {started ? ` · started ${started.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
          <h1>{started ? started.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' }) : 'Meeting'}</h1>
        </div>

        <div className="status">
          {ended ? (
            <span className="state">
              <span className="signal ended" aria-hidden="true" />
              Ended
            </span>
          ) : (
            <>
              <span className="state">
                <span className={`signal ${connection}`} aria-hidden="true" />
                {connection === 'open' ? 'Live' : connectionLabel(connection)}
                {connection === 'open' && state.startedAt !== null ? <Elapsed startedAt={state.startedAt} /> : null}
              </span>
              <span className={`worker-${state.workerStatus}`}>{workerLabel(state.workerStatus)}</span>
              <span>{state.viewerCount} watching</span>
              {role === 'viewer' && following ? (
                <span className="following">
                  <span className="signal pointer" aria-hidden="true" />
                  Following the phone
                </span>
              ) : null}
              {role === 'viewer' && !following ? (
                <button type="button" className="primary" onClick={resumeFollowing}>
                  Follow the phone
                </button>
              ) : null}
            </>
          )}
        </div>

        {state.segments.length > 0 ? (
          <div className="actions">
            <button type="button" onClick={() => void copyTranscript()}>
              {copiedTranscript ? 'Copied' : 'Copy'}
            </button>
            <button type="button" className="primary" onClick={downloadTranscript}>
              Download .md
            </button>
          </div>
        ) : null}
      </header>

      {state.error ? <p className="error">{state.error}</p> : null}

      <div className="layout">
        <aside className="speakers" aria-label="Speakers">
          <h2 className="eyebrow">In the room</h2>
          {speakerList.length > 0 ? (
            <ul>
              {speakerList.map((speaker) => {
                const percent = Math.round((shares.get(speaker.key) ?? 0) * 100);
                return (
                  <li key={speaker.key}>
                    <button
                      type="button"
                      className={`speaker-row speaker-${speakerColorIndex(speaker.key)}`}
                      onClick={() => renameSpeaker(speaker.key, speaker.displayName)}
                      title={`Rename ${speaker.displayName} for everyone`}
                    >
                      <span className="name">{speaker.displayName}</span>
                      <span className="share">{percent}%</span>
                      <span className="bar" aria-hidden="true">
                        <span style={{ width: `${percent}%` }} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <p className="hint">
            {speakerList.length > 0
              ? 'Click a name to rename that speaker for everyone.'
              : 'Speakers appear here as they talk.'}
          </p>
        </aside>

        <div className="column-wrap" {...pointerHandlers}>
          <ConversationView
            state={state}
            onRenameSpeaker={renameSpeaker}
            onGeometry={handleGeometry}
            onScroll={handleScroll}
            columnRef={(element) => {
              columnRef.current = element;
            }}
          />
          <TouchOverlay trace={trace} geometry={overlayGeometry} color={POINTER_COLOR} />
        </div>
      </div>

      {role === 'controller' && !ended ? (
        <footer className="controls">
          <span className="hint">
            <span className={`signal pointer${isPointing ? '' : ' idle'}`} aria-hidden="true" />
            {isPointing ? 'Pointing for everyone' : 'Press and drag to point'}
          </span>
          <button
            type="button"
            className="danger"
            onClick={() => {
              if (window.confirm('End the meeting for everyone?')) void endSession(sessionId, token);
            }}
          >
            End meeting
          </button>
        </footer>
      ) : null}
    </div>
  );
}

/**
 * The meeting's running time. Ticks on its own, so the transcript is not
 * re-rendered every second to move a clock.
 */
function Elapsed({ startedAt }: { startedAt: number }): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  return <time className="elapsed">{clock((now - startedAt) * 1_000)}</time>;
}

function connectionLabel(connection: string): string {
  if (connection === 'open') return 'Connected';
  if (connection === 'connecting') return 'Connecting…';
  if (connection === 'reconnecting') return 'Reconnecting…';
  return 'Disconnected';
}

function workerLabel(status: string): string {
  if (status === 'ready') return 'Transcribing';
  if (status === 'provisioning') return 'Starting transcription…';
  if (status === 'failed') return 'Worker failed';
  return 'No worker';
}

/** Re-exported so the start page can show a speaker's name consistently. */
export { speakerName };
